import { useCallback, useMemo, useRef, useState } from "react";
import { getSelectionRange, toggleSelectedElementIds } from "./utils";
import type { UseGridSelection } from "./types";

export const useGridSelection: UseGridSelection = ({ elements }) => {
  const lastToggledElementIdRef = useRef<string | null>(null);

  const [selectedElementIds, setSelectedElementIds] = useState<Set<string>>(
    new Set(),
  );

  // Indexed once per element list so shift-click ranges resolve in O(range), not O(N).
  const positionByElementId = useMemo(
    () => new Map(elements.map((element, position) => [element.id, position])),
    [elements],
  );

  const toggleElementSelection = useCallback(
    (elementId: string, isShiftRange: boolean) => {
      const anchorElementId = lastToggledElementIdRef.current;
      lastToggledElementIdRef.current = elementId;

      const selectionRange =
        isShiftRange && anchorElementId !== null
          ? getSelectionRange({
              positionByElementId,
              fromElementId: anchorElementId,
              toElementId: elementId,
            })
          : null;

      setSelectedElementIds((previous) =>
        toggleSelectedElementIds({
          previousSelectedElementIds: previous,
          clickedElementId: elementId,
          selectionRange,
          elements,
        }),
      );
    },
    [elements, positionByElementId],
  );

  const clearSelection = useCallback(() => {
    lastToggledElementIdRef.current = null;
    setSelectedElementIds(new Set());
  }, []);

  return { selectedElementIds, toggleElementSelection, clearSelection };
};
